import $ from 'jquery';
import token from './form-helper/token';
import debounce from 'throttle-debounce/debounce';
import * as validation from './validation-rules';
import Tooltip from 'tooltip.js';

const DEFAULT_JS_CLASS = 'js-form';

/**
 * Forms basic functionalities
 * See form-mailing-list.js for ajax submitting
 * @class
 */
export class Form {
  dom = {
    $form: null,
    $syrupFields: null,
    $submit: null,
    $tokenField: null,
    $fields: null,
    $cells: null
  };

  fieldsIndex = [];

  jsClass = null;

  _subscribers = [];
  _name = '';

  constructor(htmlElement, jsClass) {
    this.jsClass = jsClass || DEFAULT_JS_CLASS;

    this.dom.$form = $(htmlElement);
  }

  // Get form name
  get name() {
    return this._name;
  }

  // Set form name
  set name(name) {
    this._name = name;
  }

  initTooltips() {
    const self = this;

    this.dom.$labels.each(function () {
      const title = this.getAttribute('title');

      if(title) {
        const $label = $(this);
        const tooltipElement = this.querySelector('.' + self.jsClass + '__tooltip');
        const $cell = $label.closest('.' + self.jsClass + '__cell');

        // Prevent label click if we're on tooltip
        $label.on('click', function (e) {
          if(e.target === tooltipElement) {
            e.preventDefault();
          }
        });

        // Enable tooltip
        new Tooltip(tooltipElement, {
          html: true,
          title: title,
          trigger: 'click',
          container: document.body
        });

        // Disable default title hovering
        this.setAttribute('title', '');

        // Show tooltip
        $cell.addClass('has-tooltip');
      }
    });
  }

  /**
   * Get form's HTML element
   *
   * @returns {HTMLElement}
   */
  getElement() {
    return this.dom.$form[0];
  }

  initDonationFields() {
    const self = this;
    const $donationButtons = this.dom.$cells.find('.' + this.jsClass + '__donation-sum-button');
    const $otherSumCell = this.dom.$cells.filter('.' + this.jsClass + '__other-sum-cell');

    // Open `other_sum` cell if you `other` donation button is clicked
    $donationButtons.on('click', function () {
      const $button = $(this);

      if($button.hasClass(self.jsClass + '__donation-sum-button--other')) {
        $otherSumCell.addClass('is-shown');
      } else {
        $otherSumCell.removeClass('is-shown');
      }
    });
  }
  /**
   * Fields that need checking from server
   *
   * Ajax check fields need to have class js-form__ajax-check-field and params `data-ajax-url` and `data-ajax-field`
   *
   * Ajax JSON response needs have properties `status` and `message`. If status === 'success' everything is ok, if not, a tooltip will be shown and error gets displayed.
   * Tooltip text comes from `message` property of the json response
   */
  initAjaxCheckFields() {
    const $fields = this.dom.$cells.find('.' + this.jsClass + '__ajax-check-field');
    const self = this;

    $fields.each(function () {
      const $field = $(this);
      const ajaxUrl = $field.data('ajax-url');
      const ajaxField = $field.data('ajax-field');

      let xhr;
      let lastCheckedValue;

      const tooltip = new Tooltip($field, {
        html: true,
        title: 'Test',
        trigger: 'manual',
        closeOnClickOutside: false
      });

      const ajaxLoading = () => {
        const data = {};
        const fieldValue = $field.val();

        // Add to ajax data
        data[ajaxField] = fieldValue;

        // If something random triggered change / keyup, don't check twice in a row
        if(fieldValue === lastCheckedValue) {
          return;
        }

        // IF we're already fetching, abort
        if (xhr && xhr.readystate !== 4) {
          xhr.abort();
        }

        self.setFieldLoadingStatus($field.attr('name'), true);

        xhr = $.ajax({
          url: ajaxUrl,
          method: 'POST',
          dataType: 'json',
          data: data,
          success: (res) => {
            lastCheckedValue = fieldValue;

            if(res.status === 'success') {
              self.setCustomValidity($field.attr('name'), '');
            } else {
              tooltip.updateTitleContent(res.message);
              tooltip.show();
              self.setCustomValidity($field.attr('name'), 'Error');
            }

            self.setFieldLoadingStatus($field.attr('name'), false);
          }
        });
      }

      $field.on('change keyup', debounce(500, function () {
        self.setCustomValidity($field.attr('name'), '');
        tooltip.hide();

        // Check validity before running ajax actions
        if(self.checkFieldValidity($field)) {
          ajaxLoading();
        }
      }));
    });
  }


  /**
   * try to prevent unwanted posting
   */
  initSyrupFields() {
    const self = this;
    let fieldsShown = false;

    this.dom.$syrupFields = this.dom.$form.find('.' + self.jsClass + '__syrup');

    if (!this.dom.$syrupFields.length) {
      return;
    }

    this.dom.$form.one('focus click mousedown touchstart', function() {
      if (!fieldsShown) {
        // Add hidden fields
        self.dom.$syrupFields.each(function() {
          const $fieldInfo = $(this);
          $('<input />', {'type': 'hidden', 'value': 'u', 'name': $fieldInfo.data('name')}).appendTo(self.dom.$form);
          fieldsShown = true;
        });
      }
    });
  }

  addListeners() {
    const self = this;
    let tokenFetched = false;

    this.dom.$tokenField = this.dom.$form.find('[name="stage_token"]');
    this.dom.$submit = this.dom.$form.find('[type="submit"]');

    this.dom.$form.on('submit', function (e) {
      // Disable submit button
      self.dom.$submit.prop('disabled', true);

      if(!tokenFetched) {
        e.preventDefault();
        // Fetch new token and submit
        token.fetch().then(res => {
          self.dom.$tokenField.val(res);
          tokenFetched = true;
          self.onSubmit(e);
        }, () => {
          // Something went wrong, enable submit
          self.dom.$submit.prop('disabled', false);
        });
      }
    });

    const requiredRules = this.dom.$form.data('required-rules');

    this.dom.$fields.each(function () {
      let $this = $(this);
      const fieldName = $this.attr('name');
      const value = self.getFieldValue(fieldName);

      // Listen for field changes
      $this.on('change keyup', (e) => {
        const value = self.getFieldValue(fieldName);

        self.applyValidityClasses($this);
        self.applyValidityMessages($this);
        self.applyEmptyClasses($this, value);
        if(requiredRules && requiredRules[fieldName]) {
          self.applyRequiredRules($this, requiredRules[fieldName][value]);
        }

        self.notifySubscribers('change', fieldName, value);
      });

      // And apply based on current values
      self.applyEmptyClasses($this, value);
      self.applyValidityClasses($this);
      self.applyValidityMessages($this);

      if(requiredRules && requiredRules[fieldName]) {
        // Apply current rules
        self.applyRequiredRules($this, requiredRules[fieldName][value]);
      }
    });
  }

  onSubmit(e) {
    this.dom.$form.submit();
  }

  // Empty on purpose
  onInit() {

  }

  // Add subscribers
  on(action, callback) {
    if(typeof action === 'string' && typeof callback === 'function') {
      this._subscribers.push({
        action: action,
        callback: callback
      });
    }
  }

  /**
   * @param {Array<string>} [wantedFields] - optional: array of wanted field names
   * @returns {Object}
   */
  getFieldValues(wantedFields) {
    const self = this;
    const values = {};

    // We can't use serializeArray so that we can use this for inputs not inside form
    if(!this.dom.$fields.length) {
      return null;
    }

    this.dom.$fields.each(function () {
      const fieldName = this.getAttribute('name');

      // Only wantedFields or every field if omitted
      if(!wantedFields || wantedFields.indexOf(fieldName) !== -1) {
        const value = self.getFieldValue(fieldName);
        values[fieldName] = value;
      }
    });

    return values;
  }

  // Notify subscribers
  notifySubscribers() {
    const args = Array.prototype.slice.call(arguments);
    const action = args[0];

    // Remove action from arguments
    args.shift();

    this._subscribers.forEach(subscriber => {
      if(subscriber.action === action) {
        subscriber.callback.apply(this, args);
      }
    });
  }

  /**
   * Get field elements
   *
   * @param {string} fieldName - fieldName
   * @return {NodeList<HTMLInputElement>} NodeList of elements
   */
  getFields(fieldName) {
    return this.dom.$form[0].querySelectorAll(`[name="${fieldName}"]`);
  }

  /**
   * Get cell element
   *
   * @param {string} fieldName - field name
   * @returns {HTMLElement|null}
   */
  getCell(fieldName) {
    return this.dom.$form[0].querySelector(`[data-field-name="${fieldName}"]`);
  }

  getFieldValue(fieldName) {
    const $fields = $(this.getFields(fieldName));
    let value;

    $fields.each(function () {
      const $field = $(this);
      const type = this.getAttribute('type');

      // Handle radio buttons and checkboxes
      if(type === 'radio' || type === 'checkbox') {
        if($field.prop('checked')) {
          value = this.getAttribute('value');
          return false;
        }
      } else {
        // And everything else
        value = $field.val();
        return false;
      }
    });

    return value;
  }

  /**
   *
   * @param {Array<string>} [fieldNames] - array of field names
   * @return {Object} errors - je
   */
  getValidationErrors(fieldNames) {
    const self = this;
    const res = {};

    let $fields;

    const populateErrorObject = ($fields) => {
      $fields.each(function () {
        const fieldName = this.getAttribute('name');

        // If field doesn't pass validation rules
        if(!self.checkFieldValidity($(this))) {
          res[fieldName] = true;
        }
      })
    }

    if(fieldNames) {
      fieldNames.forEach(fieldName => {
        $fields = $(this.getFields(fieldName));

        populateErrorObject($fields);
      });
    } else {
      populateErrorObject(this.dom.$fields);
    }

    // Return res if we have errors and null if all is good
    return Object.keys(res).length ? res : null;
  }

  updateFieldValues(fieldsObj, changeEventEnabled = true) {
    Object.keys(fieldsObj).forEach(fieldName => {
      this.updateFieldValue(fieldName, fieldsObj[fieldName], changeEventEnabled);
    });
  }

  updateFieldValue(fieldName, value, changeEventEnabled = true) {
    const $fields = $(this.getFields(fieldName));

    let changed = false;
    let origVal;

    $fields.each(function () {
      const $field = $(this);

      // Handle radio buttons
      switch(this.getAttribute('type')) {
      case 'radio':
      case 'checkbox':
        if(this.getAttribute('value') === value) {
          if($field.prop('checked') !== true) {
            changed = true;
          }

          $field.prop('checked', true);
        }
        break;
      default:
        origVal = $field.val();
        // And everything else
        $field.val(value);

        if(origVal !== value) {
          changed = true;
        }
        break;
      }

      if(changeEventEnabled && changed) {
        $field.trigger('change');
      }
    });
  }

  checkFieldValidity($field) {
    const field = $field[0];

    if(field.checkValidity) {
      return field.checkValidity() && this.checkExtraValidityRules($field);
    }

    // if browser doesn't support checkValidity, allow anyway
    return true;
  }

  setFieldLoadingStatus(fieldName, loading) {
    const $fields = $(this.getFields(fieldName));
    const $cell = $fields.closest('.' + this.jsClass + '__cell');

    if(loading) {
      $cell.addClass('is-loading');
    } else {
      $cell.removeClass('is-loading');
    }

  }

  setCustomValidity(fieldName, message) {
    const $field = $(this.getFields(fieldName));
    const field = $field[0];

    if(field.setCustomValidity) {
      field.setCustomValidity(message);
    }

    this.applyValidityClasses($field);
  }

  applyValidityClasses($field) {
    const $cell = $field.closest('.' + this.jsClass + '__cell');

    if(this.checkFieldValidity($field)) {
      $cell.addClass('is-valid');
      $cell.removeClass('is-invalid');
    } else {
      $cell.addClass('is-invalid');
      $cell.removeClass('is-valid');
    }
  }

  /**
   * Shows or hides reason for input validation failure
   * Requires data-hint="" attribute on input element. Localised string from CMS.
   * Is not shown for empty fields.
   *
   * @param {jQuery} $field - jQuery dom element
   */
  applyValidityMessages($field) {
    const msgClass = 'js-form__hint';
    if($field.val() && $field.data('hint')) {
      if(this.checkFieldValidity($field)) {
        $field.siblings(`.${msgClass}`).remove();
      } else {
        const $msg = $field.siblings(`.${msgClass}`);
        if($msg.length === 0) {
          $field.after(`<div class="form__hint ${msgClass}">${$field.data('hint')}</div>`);
        }
      }
    } else {
      $field.siblings(`.${msgClass}`).remove();
    }
  }

  checkExtraValidityRules($field) {
    const fieldValue = $field.val();
    let selectionAmount = 0;

    if($field.data('min-value')) {
      const minValue = +$field.data('min-value');
      // check fo commas and replace with decimal
      const parsedValue = parseFloat(fieldValue.replace(',', '.'));

      return parsedValue >= minValue;
    }

    if($field.attr('type') === 'checkbox') {
      const name = $field.attr('name');
      selectionAmount = $('input[name=\'' + name + '\']:checked').length;
    }

    // @sample: <input data-validation="{minValue: 10, maxValue: 20}">
    // Ignores params for which a validation function is not found
    const validationRules = $field.data('validation');
    if(typeof validationRules === 'object') {
      const validationStates = [];

      Object.keys(validationRules).forEach(rule => {
        if(validation[rule]) {
          const value = rule.includes('Amount') ? selectionAmount : fieldValue;
          validationStates.push(validation[rule](value, validationRules[rule]));
        }
      });

      if(validationStates.some(val => val === false)) {
        return false;
      }
    }

    return true;
  }

  applyEmptyClasses($field, value) {
    const $cell = $field.closest('.' + this.jsClass + '__cell');

    if(value) {
      $cell.removeClass('is-empty');
      $cell.addClass('has-value');
    } else {
      $cell.addClass('is-empty');
      $cell.removeClass('has-value');
    }
  }

  initRequiredRules() {
    const rules = this.dom.$form.data('required-rules');
    const self = this;

    if (!rules) {
      return;
    }

    this.dom.$fields.each(function () {
      let $this = $(this);
      const fieldName = $this.attr('name');

      // If we have rules for this field
      if(rules[fieldName]) {
        // Listen for changes
        $this.on('change', () => {
          self.applyRequiredRules($this, rules[fieldName][$this.val()]);
        });

        // And apply current rules
        self.applyRequiredRules($this, rules[fieldName][$this.val()]);
      }
    });
  }

  changeFieldRequiredStatus($field, required) {
    if (!$field.length) {
      return;
    }

    const $cell = $field.closest('.' + this.jsClass + '__cell');

    if (required) {
      $cell.addClass('is-required');
    } else {
      $cell.removeClass('is-required');
    }

    $field.prop('required', required);
  }

  applyRequiredRules($field, rules) {
    const self = this;

    if(!rules) {
      return;
    }

    // Only if this radio or checkbox is checked
    if($field.attr('type') === 'radio' || $field.attr('type') === 'checkbox') {
      if(!$field.is(':checked')) {
        return;
      }
    }

    this.dom.$fields.each(function () {
      const $this = $(this);

      if(typeof rules[$this.attr('name')] === 'boolean') {
        self.changeFieldRequiredStatus($this, rules[$this.attr('name')])
      }
    });
  }

  // Store field index to object
  _populateFieldsIndex() {
    const self = this;

    this.dom.$fields.each(function () {
      self.fieldsIndex.push(this.getAttribute('name'));
    });
  }

  init() {
    if (!this.dom.$form.length) {
      return;
    }

    this.dom.$cells = this.dom.$form.find('.' + this.jsClass + '__cell');
    this.dom.$labels = this.dom.$cells.find('.' + this.jsClass + '__label');
    this.dom.$fields = this.dom.$form.find('.' + this.jsClass + '__field');

    this._populateFieldsIndex();
    this.initSyrupFields();
    this.addListeners();
    this.initRequiredRules();
    this.initDonationFields();
    this.initAjaxCheckFields();
    this.initTooltips();

    this.onInit();
  }
}
